import { useState, useEffect, useCallback } from "react";
import { Trophy, Ticket, Search, DollarSign } from "lucide-react";
import api from "../../services/api";

function fmt(n) { return n == null ? "0" : n.toLocaleString("pt-BR"); }
function fmtR(v) { return v == null ? "R$ 0" : "R$ " + v.toLocaleString("pt-BR", { minimumFractionDigits: 2 }); }
function fmtD(d) { return d ? new Date(d).toLocaleDateString("pt-BR") : "-"; }

export default function DoletasGalaAdmin() {
  var _tickets = useState([]);
  var tickets = _tickets[0];
  var _stats = useState([]);
  var itemStats = _stats[0];
  var _totalRev = useState(0);
  var totalRevenue = _totalRev[0];
  var _loading = useState(true);
  var loading = _loading[0];
  var _search = useState("");
  var search = _search[0];
  var _itemFilter = useState("");
  var itemFilter = _itemFilter[0];

  var fetchData = useCallback(async function () {
    _loading[1](true);
    try {
      var params = new URLSearchParams();
      if (itemFilter) params.append("item_name", itemFilter);
      if (search) params.append("search", search);
      var res = await api.get("/doletas/gala/tickets?" + params.toString());
      _tickets[1](res.data.tickets || []);
      _stats[1](res.data.item_stats || []);
      _totalRev[1](res.data.total_revenue || 0);
    } catch (err) {
      console.error(err);
    } finally {
      _loading[1](false);
    }
  }, [itemFilter, search]);

  useEffect(function () { fetchData(); }, [fetchData]);

  return (
    <div className="space-y-6" data-testid="doletas-gala-admin">
      {/* Revenue Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        {itemStats.map(function (s) {
          return (
            <div key={s.item} className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Ticket className="w-4 h-4 text-[#F26A4B]" />
                <span className="text-xs text-gray-500 font-medium truncate">{s.item}</span>
              </div>
              <p className="text-xl font-bold text-[#0A2F3D]">{fmt(s.tickets_sold)}</p>
              <p className="text-xs text-gray-400">tickets vendidos</p>
              <p className="text-sm font-medium text-emerald-600 mt-1">{fmtR(s.revenue)}</p>
            </div>
          );
        })}
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4" />
            <span className="text-xs font-medium text-white/80">Receita Total</span>
          </div>
          <p className="text-2xl font-bold" data-testid="gala-total-revenue">{fmtR(totalRevenue)}</p>
          <p className="text-xs text-white/60">{tickets.length} tickets no total</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" placeholder="Buscar por nome ou ticket..." value={search}
            onChange={function (e) { _search[1](e.target.value); }}
            className="pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm w-full" data-testid="gala-search" />
        </div>
        <select value={itemFilter} onChange={function (e) { _itemFilter[1](e.target.value); }}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="gala-item-filter">
          <option value="">Todos os Itens</option>
          <option value="iPhone">iPhone</option>
          <option value="1 ano de consorcio pago (R$500/mes)">Consorcio 1 ano</option>
          <option value="Notebook">Notebook</option>
          <option value="Patinete Eletrico">Patinete Eletrico</option>
        </select>
      </div>

      {/* Tickets Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <h3 className="font-semibold text-[#0A2F3D] text-sm flex items-center gap-2">
            <Trophy className="w-4 h-4 text-amber-500" />Tickets Vendidos ({tickets.length})
          </h3>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12"><div className="w-6 h-6 border-2 border-[#3B82F6] border-t-transparent rounded-full animate-spin" /></div>
        ) : tickets.length === 0 ? (
          <div className="py-12 text-center text-gray-400 text-sm">Nenhum ticket vendido ainda</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-4 py-3 text-xs font-medium text-gray-500">Ticket</th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-500">Comprador</th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-500">Tipo</th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-500">Item</th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-500 text-right">Valor</th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-500">Data</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {tickets.map(function (t) {
                  return (
                    <tr key={t.id} className="hover:bg-gray-50" data-testid={"ticket-row-" + t.id}>
                      <td className="px-4 py-3 font-mono text-xs text-[#3B82F6]">{t.ticket_number}</td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-[#0A2F3D]">{t.user_name}</p>
                        <p className="text-xs text-gray-400">{t.user_role === "broker" ? "Vendedor" : "Cliente"}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={"px-2 py-1 text-xs font-medium rounded-full " + (t.user_role === "broker" ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700")}>
                          {t.user_role === "broker" ? "Vendedor" : "Cliente"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{t.item_name}</td>
                      <td className="px-4 py-3 text-right font-medium text-[#0A2F3D]">{fmtR(t.ticket_price)}</td>
                      <td className="px-4 py-3 text-gray-400 text-xs">{fmtD(t.purchased_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
